import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** A row of page tabs under a header; the active one is underlined in the accent. */
export function Tabs<T extends string>({ value, onChange, tabs, className }: {
  value: T;
  onChange: (v: T) => void;
  tabs: Array<{ value: T; label: string; count?: number; icon?: ReactNode }>;
  className?: string;
}) {
  return (
    <div role="tablist" className={cn('scroll-thin -mb-px flex gap-1 overflow-x-auto border-b border-line', className)}>
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              'flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
              active ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.icon}
            {t.label}
            {t.count !== undefined && <span className={cn('rounded-full px-1.5 text-[11px] tabular-nums', active ? 'bg-accent-soft text-accent-ink' : 'bg-surface-2 text-muted')}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
